/*
 * Module controls refreshing the current KB on the server, as well as getting and displaying the last
 * time the KB was updated
 */

define([
  "marionette",
  "jquery",
  "moment",
  "messaging/messenger",
  "modules/header/kbRefreshMenu/kbRefreshMenuView",
  "modules/header/kbRefreshMenu/kbRefreshMenuModel",
], function (Marionette, $, Moment, Messenger, KbRefreshMenuView) {
  "use strict";

  const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;
  const ONE_HOUR_IN_MILLISECONDS = 60 * ONE_MINUTE_IN_MILLISECONDS;
  const ONE_DAY_IN_MILLISECONDS = 24 * ONE_HOUR_IN_MILLISECONDS;
  const FETCH_INTERVAL_IN_MINUTES = 10;
  const LARGE_WAIT_TIME = ONE_MINUTE_IN_MILLISECONDS;
  const SMALL_WAIT_TIME = 5 * 1000;
  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "kbRefreshMenu";
      this.region = options.region;
      this._setUpVents();
    },
    show: function () {
      this._clearTimers();
      this.refreshModel = Messenger.reqres.request("active:kb:refresh:model");
      this.view = new KbRefreshMenuView.kbRefreshMenuView({ model: this.refreshModel });
      this.view.on(
        "refresh:kb",
        function () {
          // only make a refresh request when one isn't already occurring and the KB is loaded
          if (!this.refreshModel.updating && this.refreshModel.get("loaded")) {
            // save whatever we're doing before firing the request, that way, we don't "miss"
            // anything in the refresh because it wasn't saved yet
            Messenger.save((success) => {
              if (success) {
                this._handleRefresh();
              }
            });
          }
        },
        this,
      );
      this.refreshModel.onSynced(() => {
        this._setFetchTimer();
        this._setDateField();
        this.showRegion(this.view);
      });
    },
    _clearFetchTimer: function () {
      if (this.fetchTimer) {
        window.clearInterval(this.fetchTimer);
        this.fetchTimer = false;
      }
    },
    _clearMidnightTimer: function () {
      if (this.midnightTimer) {
        window.clearTimeout(this.midnightTimer);
        this.midnightTimer = false;
      }
    },
    _clearMinuteTimer: function () {
      if (this.minuteTimer) {
        window.clearTimeout(this.minuteTimer);
        this.minuteTimer = false;
      }
    },
    _clearTimers: function () {
      this._clearMidnightTimer();
      this._clearMinuteTimer();
      this._clearFetchTimer();
      this._clearUpdateTimer();
    },
    _clearUpdateTimer: function () {
      if (this.updateTimer) {
        window.clearInterval(this.updateTimer);
        this.updateTimer = false;
      }
    },
    // POST the update request to the server and initiate client side polling if the
    // request was successful
    _handleRefresh: function () {
      console.log("refresh kb");
      const averageRefreshTime = this.refreshModel.get("avgRefreshMillis");
      this.refreshModel.updating = true;
      this.view.render();
      const updateModel = this.refreshModel.update();
      const refreshFailed = () => {
        this.refreshModel.updating = false;
        this.refreshModel.fetch(); //get the latest refresh info
        this.refreshModel.onSynced(() => this.view.render());
        Messenger.okDialog("header.kbRefresh.error");
      };
      const pollServer = () => {
        updateModel.fetch({
          error: refreshFailed,
          success: (model) => {
            if (model.isFailed()) {
              refreshFailed();
            } else if (model.isSuccessful()) {
              this.refreshModel.updating = false;
              this.refreshModel.fetch(); //get the latest refresh info
              this.refreshModel.onSynced(() => this.view.render());
            } else {
              // For short KB refreshes, poll often with a short delay.
              // For longer KB refreshes, poll less often with a longer delay.
              let pollTimeout;
              if (averageRefreshTime > LARGE_WAIT_TIME) {
                pollTimeout = LARGE_WAIT_TIME;
              } else {
                pollTimeout = SMALL_WAIT_TIME;
              }
              this.updateTimer = window.setTimeout(pollServer, pollTimeout);
            }
          },
        });
      };
      // stop updating the UI while refreshing
      this._clearMinuteTimer();
      this._clearMidnightTimer();
      updateModel.save(null, {
        success: () => {
          this.view.showProgressBar(averageRefreshTime);
          this.updateTimer = window.setTimeout(
            pollServer,
            Math.min(averageRefreshTime, LARGE_WAIT_TIME),
          );
        },
        error: refreshFailed,
      });
    },
    _setDateField: function () {
      // if the kb is loaded, set the formatted date and define timers for when the date needs
      // to be updated locally (e.g. incrementing minutes or days)
      if (!this.refreshModel.get("loaded")) {
        return;
      }
      const delayFunction = () => {
        this._clearMidnightTimer();
        this._setDateField();
        this.view.render();
      };
      const timestamp = this.refreshModel.get("lastUpdateTimestamp");
      // if timestamp is within an hour, write the number of minutes that have elapsed
      // instead of the date
      const now = Date.now();
      let delay;
      if (now - timestamp < ONE_HOUR_IN_MILLISECONDS) {
        const minutes = Math.max(0, Math.floor((now - timestamp) / ONE_MINUTE_IN_MILLISECONDS));
        this.refreshModel.date = $.t("header.kbRefresh.minutesAgo", { count: minutes });
        // set automatic rerender when the "next minute" occurs (small rounding up to make sure
        // it happens after the next minute, and not JUST before)
        delay =
          1.02 * (ONE_MINUTE_IN_MILLISECONDS - ((now - timestamp) % ONE_MINUTE_IN_MILLISECONDS));
        //console.log("delay (s): " + delay/1000);
        this.minuteTimer = window.setTimeout(delayFunction, delay);
      } else {
        const dateObj = new Date(timestamp);
        // if the update was today or yesterday, use 'today' or 'yesterday' instead of the date
        const nowDate = new Date(now);
        const yesterdayDate = new Date(now - ONE_DAY_IN_MILLISECONDS);
        let isToday = false;
        let isYesterday = false;
        if (
          nowDate.getMonth() === dateObj.getMonth() &&
          nowDate.getFullYear() === dateObj.getFullYear() &&
          nowDate.getDate() === dateObj.getDate()
        ) {
          isToday = true;
        } else if (
          yesterdayDate.getMonth() === dateObj.getMonth() &&
          yesterdayDate.getFullYear() === dateObj.getFullYear() &&
          yesterdayDate.getDate() === dateObj.getDate()
        ) {
          isYesterday = true;
        }
        // if we are showing 'today' or 'yesterday', then we need to set a timer to run
        // at midnight to rerender this area (because today isn't today anymore!)
        if (!this.midnightTimer && (isToday || isYesterday)) {
          // delay is the # of milliseconds until midnight
          // (uses timezone offset to convert from UTC to local timezone)
          delay =
            ONE_DAY_IN_MILLISECONDS -
            ((now - nowDate.getTimezoneOffset() * ONE_MINUTE_IN_MILLISECONDS) %
              ONE_DAY_IN_MILLISECONDS);
          this.midnightTimer = window.setTimeout(delayFunction, delay);
        }
        const momentDate = Moment(dateObj);
        const timeString = Messenger.reqres.request("get:formatted:time", momentDate);
        if (isToday) {
          this.refreshModel.date = $.t("header.kbRefresh.today") + " " + timeString;
        } else if (isYesterday) {
          this.refreshModel.date = $.t("header.kbRefresh.yesterday") + " " + timeString;
        } else {
          const dateString = Messenger.reqres.request("get:formatted:date", momentDate);
          this.refreshModel.date = dateString + " " + timeString;
        }
      }
    },
    _setFetchTimer: function () {
      const delayFunction = () => {
        if (!this.refreshModel.updating) {
          this.refreshModel.fetch();
        }
      };
      // update area and resync minute timer if a change is registered (from the update)
      this.refreshModel.on(
        "change",
        function () {
          //console.log("update change");
          this._clearMinuteTimer();
          this._clearMidnightTimer();
          this._setDateField();
          this.view.render();
        },
        this,
      );
      // fetch a new refresh model every FETCH_INTERVAL_IN_MINUTES from the server
      this.fetchTimer = window.setInterval(
        delayFunction,
        FETCH_INTERVAL_IN_MINUTES * ONE_MINUTE_IN_MILLISECONDS,
      );
    },
    _setUpVents: function () {
      // update this area when the kb is changed
      Messenger.vent.on(
        "get:refresh:status",
        function () {
          this.show();
        },
        this,
      );
    },
  });
});
